import { useEffect, useState, type CSSProperties } from "react";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { idToken } from "../auth/cognito";
import { AdminCard } from "../components/AdminCard";
import { Btn } from "../components/Btn";
import { Field } from "../components/Field";
import { Input } from "../components/Input";
import { SectionHead } from "../components/SectionHead";
import { Stat } from "../components/Stat";
import type { AboutResponse, AdminComment, AdminCommentsResponse, AdminPledgersResponse, AdminRidesResponse, CloseFundraiserResponse, Ride, RidePhoto, SiteConfig, StravaStatus, SyncResult, UploadUrlResponse } from "../api/types";
import { money, money2 } from "../lib/format";

const EMPTY_COPY: SiteConfig = {
  heroCopy: "",
  heroSubcopy: "",
  heroImage: "",
  charity: "",
  gofundme: "",
  goal: 1000,
  aboutEyebrow: "",
  aboutHeadline: "",
  aboutParagraphs: [],
  aboutFaqs: [],
  rideStartDate: "",
  ridePhotos: {},
  summaryEmailPaused: false,
};

export function Admin() {
  const { user } = useAuth();
  const [pledgers, setPledgers] = useState<AdminPledgersResponse | null>(null);
  const [strava, setStrava] = useState<StravaStatus | null>(null);
  const [copy, setCopy] = useState<SiteConfig>(EMPTY_COPY);
  const [savingCopy, setSavingCopy] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [stravaError, setStravaError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [rides, setRides] = useState<Ride[]>([]);
  const [selectedRideId, setSelectedRideId] = useState<string>("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeResult, setCloseResult] = useState<CloseFundraiserResponse | null>(null);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [reopening, setReopening] = useState(false);
  const [comments, setComments] = useState<AdminComment[] | null>(null);

  const refresh = async () => {
    const [p, s, c] = await Promise.all([
      api.get<AdminPledgersResponse>("/admin/pledgers"),
      api.get<StravaStatus>("/admin/strava/status"),
      api.get<AdminCommentsResponse>("/admin/comments"),
    ]);
    setPledgers(p);
    setStrava(s);
    setComments(c.comments);
    const [about, ridesResp] = await Promise.all([
      api.get<AboutResponse>("/public/about"),
      api.get<AdminRidesResponse>("/admin/rides"),
    ]);
    setRides(ridesResp.rides);
    setSelectedRideId((cur) => cur || ridesResp.rides[0]?.rideId || "");
    setCopy({
      heroCopy: about.heroCopy,
      heroSubcopy: about.heroSubcopy,
      heroImage: about.heroImage || "",
      charity: about.charity,
      gofundme: about.gofundme,
      goal: p.goal,
      aboutEyebrow: about.aboutEyebrow,
      aboutHeadline: about.aboutHeadline,
      aboutParagraphs: about.aboutParagraphs,
      aboutFaqs: about.aboutFaqs,
      rideStartDate: p.rideStartDate || "",
      ridePhotos: about.ridePhotos || {},
      summaryEmailPaused: Boolean(about.summaryEmailPaused),
    });
  };

  useEffect(() => {
    refresh().catch(() => {});
  }, []);

  const connectStrava = async () => {
    setStravaError(null);
    try {
      const { authorizeUrl } = await api.get<{ authorizeUrl: string }>("/admin/strava/connect");
      window.location.href = authorizeUrl;
    } catch (e) {
      setStravaError((e as Error).message);
    }
  };

  const disconnectStrava = async () => {
    await api.post("/admin/strava/disconnect");
    await refresh();
  };

  const syncNow = async () => {
    setSyncing(true);
    setStravaError(null);
    try {
      // Surface the sync result instead of discarding it — a failed sync used
      // to render exactly like a successful one, which made a dead Strava
      // connection look healthy from the admin panel.
      const r = await api.post<SyncResult>("/admin/strava/sync");
      if (!r.ok) {
        setStravaError(r.error ? `${r.reason ?? "sync failed"} — ${r.error}` : r.reason ?? "sync failed");
      }
      await refresh();
    } catch (e) {
      setStravaError((e as Error).message);
    } finally {
      setSyncing(false);
    }
  };

  const saveCopy = async () => {
    setSavingCopy(true);
    setSaveMsg(null);
    try {
      await api.put("/admin/site-copy", copy);
      setSaveMsg("Saved");
      setTimeout(() => setSaveMsg(null), 2500);
    } catch (e) {
      setSaveMsg(`Error: ${(e as Error).message}`);
    } finally {
      setSavingCopy(false);
    }
  };

  const uploadHero = async (file: File) => {
    setUploadError(null);
    setUploading(true);
    try {
      const { uploadUrl, publicUrl, contentType } = await api.post<UploadUrlResponse>(
        "/admin/media/upload-url",
        { contentType: file.type },
      );
      const put = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "content-type": contentType },
        body: file,
      });
      if (!put.ok) throw new Error(`S3 upload failed (${put.status})`);
      // Every upload writes to a unique key, so CloudFront never serves stale bytes.
      setCopy((c) => ({ ...c, heroImage: publicUrl }));
    } catch (e) {
      setUploadError((e as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const updateParagraph = (i: number, v: string) => {
    setCopy({ ...copy, aboutParagraphs: copy.aboutParagraphs.map((p, idx) => (idx === i ? v : p)) });
  };
  const addParagraph = () => setCopy({ ...copy, aboutParagraphs: [...copy.aboutParagraphs, ""] });
  const removeParagraph = (i: number) => {
    setCopy({ ...copy, aboutParagraphs: copy.aboutParagraphs.filter((_, idx) => idx !== i) });
  };
  const moveParagraph = (i: number, dir: -1 | 1) => {
    const next = [...copy.aboutParagraphs];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setCopy({ ...copy, aboutParagraphs: next });
  };

  const updateFaq = (i: number, key: "q" | "a", v: string) => {
    setCopy({ ...copy, aboutFaqs: copy.aboutFaqs.map((f, idx) => (idx === i ? { ...f, [key]: v } : f)) });
  };
  const addFaq = () => setCopy({ ...copy, aboutFaqs: [...copy.aboutFaqs, { q: "", a: "" }] });
  const removeFaq = (i: number) => {
    setCopy({ ...copy, aboutFaqs: copy.aboutFaqs.filter((_, idx) => idx !== i) });
  };
  const moveFaq = (i: number, dir: -1 | 1) => {
    const next = [...copy.aboutFaqs];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setCopy({ ...copy, aboutFaqs: next });
  };

  // ── Ride photos ──────────────────────────────────────────
  // Photos are keyed by rideId on copy.ridePhotos. The whole map round-trips on
  // save (the site-config save is a shallow merge), so we mutate it immutably.
  const ridePhotosFor = (rideId: string): RidePhoto[] => copy.ridePhotos[rideId] ?? [];

  const setRidePhotos = (rideId: string, next: RidePhoto[]) => {
    setCopy((c) => {
      const map = { ...c.ridePhotos };
      if (next.length === 0) delete map[rideId];
      else map[rideId] = next;
      return { ...c, ridePhotos: map };
    });
  };

  const appendRidePhotos = (rideId: string, additions: RidePhoto[]) => {
    if (additions.length === 0) return;
    setCopy((c) => {
      const map = { ...c.ridePhotos };
      map[rideId] = [...(map[rideId] ?? []), ...additions];
      return { ...c, ridePhotos: map };
    });
  };

  const uploadRidePhotos = async (rideId: string, files: File[]) => {
    if (files.length === 0) return;
    setPhotoError(null);
    setPhotoUploading(true);
    try {
      // Upload in parallel, then append all at once — a per-file state update
      // would read a stale photo list and drop earlier additions.
      const results = await Promise.allSettled(
        files.map(async (file): Promise<RidePhoto> => {
          const { uploadUrl, publicUrl, contentType } = await api.post<UploadUrlResponse>(
            "/admin/media/upload-url",
            { contentType: file.type, kind: "ride" },
          );
          const put = await fetch(uploadUrl, {
            method: "PUT",
            headers: { "content-type": contentType },
            body: file,
          });
          if (!put.ok) throw new Error(`S3 upload failed (${put.status})`);
          return { url: publicUrl, caption: "" };
        }),
      );
      const uploaded = results
        .filter((r): r is PromiseFulfilledResult<RidePhoto> => r.status === "fulfilled")
        .map((r) => r.value);
      appendRidePhotos(rideId, uploaded);
      const failed = results.length - uploaded.length;
      if (failed > 0) {
        setPhotoError(`${failed} of ${results.length} photo${results.length === 1 ? "" : "s"} failed to upload.`);
      }
    } finally {
      setPhotoUploading(false);
    }
  };

  const updatePhotoCaption = (rideId: string, i: number, caption: string) => {
    setRidePhotos(rideId, ridePhotosFor(rideId).map((p, idx) => (idx === i ? { ...p, caption } : p)));
  };
  const removeRidePhoto = (rideId: string, i: number) => {
    setRidePhotos(rideId, ridePhotosFor(rideId).filter((_, idx) => idx !== i));
  };
  const moveRidePhoto = (rideId: string, i: number, dir: -1 | 1) => {
    const next = [...ridePhotosFor(rideId)];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setRidePhotos(rideId, next);
  };

  const closeFundraiser = async () => {
    setClosing(true);
    setCloseError(null);
    try {
      const result = await api.post<CloseFundraiserResponse>("/admin/close-fundraiser");
      setCloseResult(result);
      setConfirmClose(false);
      await refresh();
    } catch (e) {
      setCloseError((e as Error).message);
    } finally {
      setClosing(false);
    }
  };

  const reopenFundraiser = async () => {
    setReopening(true);
    setCloseError(null);
    try {
      await api.post("/admin/reopen-fundraiser");
      setCloseResult(null);
      await refresh();
    } catch (e) {
      setCloseError((e as Error).message);
    } finally {
      setReopening(false);
    }
  };

  const setCommentHidden = async (commentId: string, hidden: boolean) => {
    await api.post("/admin/comments/hide", { commentId, hidden });
    setComments((cs) => cs?.map((c) => (c.commentId === commentId ? { ...c, hidden } : c)) ?? null);
  };

  const removeComment = async (commentId: string) => {
    await api.post("/admin/comments/delete", { commentId });
    setComments((cs) => cs?.filter((c) => c.commentId !== commentId) ?? null);
  };

  const downloadCsv = async () => {
    const token = await idToken();
    const url = `${import.meta.env.VITE_API_BASE_URL}/admin/pledgers.csv`;
    const resp = await fetch(url, { headers: token ? { authorization: `Bearer ${token}` } : {} });
    const blob = await resp.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = "pledgers.csv";
    a.click();
    URL.revokeObjectURL(href);
  };

  if (!user) return null;

  return (
    <div style={{ padding: "40px 56px 80px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 8px", background: "var(--ink)", color: "var(--bg)", borderRadius: 4 }}>
            ADMIN
          </span>
          <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>{user.email}</span>
        </div>
        <h1 style={{ margin: "8px 0 0", fontFamily: "var(--display)", fontSize: 36, fontWeight: 400, letterSpacing: -0.8 }}>
          Dashboard
        </h1>

        <div style={{ marginTop: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <AdminCard title="Strava connection">
            {strava?.connected ? (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 999, background: "#FC4C02", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>S</div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>Connected</div>
                    <div style={{ fontSize: 12, color: "var(--ink-soft)" }}>
                      Last sync: {strava.lastSyncAt ? new Date(strava.lastSyncAt).toLocaleString() : "never"}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn size="sm" variant="primary" onClick={syncNow} disabled={syncing}>{syncing ? "Syncing…" : "Sync now"}</Btn>
                  <Btn size="sm" variant="secondary" onClick={disconnectStrava}>Disconnect</Btn>
                </div>
                {(stravaError || strava.lastSyncError) && (
                  <div style={{ marginTop: 8, fontSize: 12, color: "oklch(0.50 0.16 30)" }}>
                    {stravaError ? `Sync failed: ${stravaError}` : `Last scheduled sync failed: ${strava.lastSyncError}`}
                    {strava.lastSyncAttemptAt && (
                      <span style={{ color: "var(--ink-soft)" }}>
                        {" "}(last attempt {new Date(strava.lastSyncAttemptAt).toLocaleString()})
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 14, color: "var(--ink-soft)", marginBottom: 12 }}>
                  Not connected. Miles will not auto-update.
                </div>
                <Btn size="md" onClick={connectStrava} style={{ background: "#FC4C02", borderColor: "#FC4C02", color: "#fff" }}>
                  Connect Strava
                </Btn>
                {stravaError && (
                  <div style={{ marginTop: 8, fontSize: 12, color: "oklch(0.50 0.16 30)" }}>
                    Error: {stravaError}
                  </div>
                )}
              </div>
            )}
          </AdminCard>

          <AdminCard title="Snapshot">
            {pledgers ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Stat value={pledgers.miles} label="Miles" />
                <Stat value={money(pledgers.pledgers.reduce((s, p) => s + p.owedNow, 0))} label="Pledged" accent />
                <Stat value={money(pledgers.pledgers.reduce((s, p) => s + p.projected, 0))} label={`If Scott hits ${pledgers.goal.toLocaleString()} mi`} accent />
                <Stat value={pledgers.pledgers.length} label="Pledgers" />
                <Stat value={pledgers.goal} label="Goal (mi)" />
              </div>
            ) : (
              "Loading…"
            )}
          </AdminCard>
        </div>

        <div style={{ marginTop: 24 }}>
          <AdminCard title="Fundraiser">
            {pledgers?.closed ? (
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                  ✓ The fundraiser is closed.
                </div>
                <div style={{ fontSize: 13, color: "var(--ink-soft)", marginBottom: 12 }}>
                  {closeResult
                    ? `Sent ${closeResult.emailsSent} of ${closeResult.pledgerCount} donor emails.`
                    : "Donors see the closed banner and their donation total on every page."}
                </div>
                <Btn size="sm" variant="secondary" onClick={reopenFundraiser} disabled={reopening}>
                  {reopening ? "Re-opening…" : "Re-open fundraiser"}
                </Btn>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 13, color: "var(--ink-soft)", marginBottom: 12, lineHeight: 1.5 }}>
                  Closing the fundraiser emails every pledger their donation total and the GoFundMe
                  link, then shows a site-wide "ride is over" notice. This emails{" "}
                  <strong>{pledgers?.pledgers.length ?? 0}</strong> {pledgers?.pledgers.length === 1 ? "donor" : "donors"}.
                </div>
                {!confirmClose ? (
                  <Btn size="md" variant="primary" onClick={() => setConfirmClose(true)} disabled={!pledgers}>
                    Close fundraiser & send emails
                  </Btn>
                ) : (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      Send emails to {pledgers?.pledgers.length ?? 0} {pledgers?.pledgers.length === 1 ? "donor" : "donors"}?
                    </span>
                    <Btn size="sm" variant="primary" onClick={closeFundraiser} disabled={closing}>
                      {closing ? "Closing…" : "Yes, close & send"}
                    </Btn>
                    <Btn size="sm" variant="secondary" onClick={() => setConfirmClose(false)} disabled={closing}>
                      Cancel
                    </Btn>
                  </div>
                )}
              </div>
            )}
            {closeError && (
              <div style={{ marginTop: 10, fontSize: 12, color: "var(--accent)" }}>Error: {closeError}</div>
            )}
          </AdminCard>
        </div>

        <div style={{ marginTop: 24 }}>
          <SectionHead
            title="Pledgers"
            action={<Btn size="sm" variant="secondary" onClick={downloadCsv}>Export CSV</Btn>}
          />
          <div style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 14, overflow: "hidden" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.4fr 1.6fr 1fr 1fr 1fr",
                gap: 12,
                padding: "12px 16px",
                fontSize: 11,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                color: "var(--ink-soft)",
                background: "var(--bg)",
                borderBottom: "1px solid var(--line)",
              }}
            >
              <div>Name</div>
              <div>Email</div>
              <div>Rate</div>
              <div>Cap / flat</div>
              <div style={{ textAlign: "right" }}>Donation</div>
            </div>
            {pledgers?.pledgers.map((p) => (
              <div
                key={p.userId}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.4fr 1.6fr 1fr 1fr 1fr",
                  gap: 12,
                  padding: "12px 16px",
                  alignItems: "center",
                  fontSize: 13.5,
                  borderBottom: "1px solid var(--line)",
                }}
              >
                <div style={{ fontWeight: 600 }}>{p.first} {p.last}</div>
                <div style={{ color: "var(--ink-soft)", fontSize: 12.5 }}>{p.email}</div>
                <div>{money2(p.rate)}/mi</div>
                <div style={{ color: "var(--ink-soft)", fontSize: 12.5 }}>
                  {p.cap ? `${money(p.cap)} cap` : "—"}
                  {p.flat ? ` · +${money(p.flat)}` : ""}
                </div>
                <div style={{ textAlign: "right", fontWeight: 600 }}>{money(p.owedNow)}</div>
              </div>
            ))}
            {pledgers && pledgers.pledgers.length === 0 && (
              <div style={{ padding: 28, textAlign: "center", color: "var(--ink-soft)" }}>No pledgers yet.</div>
            )}
          </div>
        </div>

        <div style={{ marginTop: 24 }}>
          <AdminCard title="Guest book">
            <div style={{ fontSize: 13, color: "var(--ink-soft)", marginBottom: 12, lineHeight: 1.5 }}>
              Messages signed-in visitors leave on the homepage. Hide one to pull it from public view
              (reversible), or delete it permanently.
            </div>
            {comments === null && <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>Loading…</div>}
            {comments && comments.length === 0 && (
              <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>No messages yet.</div>
            )}
            {comments?.map((c) => (
              <div
                key={c.commentId}
                style={{
                  padding: 12,
                  marginBottom: 10,
                  border: "1px solid var(--line)",
                  borderRadius: 10,
                  background: c.hidden ? "var(--bg)" : "transparent",
                  opacity: c.hidden ? 0.6 : 1,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>
                    {c.first} {c.last}
                    <span style={{ fontWeight: 400, color: "var(--ink-soft)" }}>
                      {" · "}
                      {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ""}
                      {c.hidden ? " · hidden" : ""}
                    </span>
                  </span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => setCommentHidden(c.commentId, !c.hidden)} style={miniBtnStyle}>
                      {c.hidden ? "Unhide" : "Hide"}
                    </button>
                    <button onClick={() => removeComment(c.commentId)} style={{ ...miniBtnStyle, color: "var(--accent)" }}>
                      Delete
                    </button>
                  </div>
                </div>
                <div style={{ fontSize: 14, color: "var(--ink)", whiteSpace: "pre-wrap" }}>{c.message}</div>
              </div>
            ))}
          </AdminCard>
        </div>

        <div style={{ marginTop: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <AdminCard title="Site copy">
            <Field label="Hero headline">
              <textarea
                value={copy.heroCopy}
                onChange={(e) => setCopy({ ...copy, heroCopy: e.target.value })}
                style={textareaStyle(80)}
              />
            </Field>
            <Field label="Hero subtext" hint="The paragraph shown under the headline on the homepage.">
              <textarea
                value={copy.heroSubcopy}
                onChange={(e) => setCopy({ ...copy, heroSubcopy: e.target.value })}
                style={textareaStyle(120)}
              />
            </Field>
            <Field label="Hero photo" hint="JPEG, PNG, or WebP. Replaces the placeholder next to the headline.">
              {copy.heroImage && (
                <img
                  src={copy.heroImage}
                  alt="Current hero"
                  style={{ display: "block", width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: 10, border: "1px solid var(--line)", marginBottom: 10 }}
                />
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={uploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadHero(f);
                    e.target.value = "";
                  }}
                  style={{ fontSize: 13 }}
                />
                {uploading && <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>Uploading…</span>}
                {copy.heroImage && !uploading && (
                  <button
                    onClick={() => setCopy({ ...copy, heroImage: "" })}
                    style={{ ...miniBtnStyle, color: "var(--accent)" }}
                  >
                    Remove
                  </button>
                )}
              </div>
              {uploadError && (
                <div style={{ marginTop: 6, fontSize: 12, color: "var(--accent)" }}>Error: {uploadError}</div>
              )}
            </Field>
            <Field label="Goal (miles)">
              <Input type="number" value={String(copy.goal)} onChange={(v) => setCopy({ ...copy, goal: Number(v) || 0 })} />
            </Field>
            <Field label="Biweekly pledger email">
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "var(--ink)", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={copy.summaryEmailPaused}
                  onChange={(e) => setCopy({ ...copy, summaryEmailPaused: e.target.checked })}
                  style={{ width: 16, height: 16, accentColor: "var(--accent)", flex: "0 0 auto", cursor: "pointer" }}
                />
                Pause the biweekly ride summary email
              </label>
              <div style={{ marginTop: 6, fontSize: 12, color: "var(--ink-soft)" }}>
                While paused, the Sunday email job skips sending. Unpausing resumes the normal every-other-week cadence.
              </div>
            </Field>
            <Field label="Campaign start date">
              <Input type="date" value={copy.rideStartDate} onChange={(v) => setCopy({ ...copy, rideStartDate: v })} />
              <div style={{ marginTop: 6, fontSize: 12, color: "var(--ink-soft)" }}>
                Only rides on or after this date count toward the miles total and show on the site. Leave blank to count every synced ride.
              </div>
            </Field>
          </AdminCard>
          <AdminCard title="Charity & GoFundMe">
            <Field label="Charity name">
              <Input value={copy.charity} onChange={(v) => setCopy({ ...copy, charity: v })} />
            </Field>
            <Field label="GoFundMe URL">
              <Input value={copy.gofundme} onChange={(v) => setCopy({ ...copy, gofundme: v })} />
            </Field>
          </AdminCard>
        </div>

        <div style={{ marginTop: 24 }}>
          <AdminCard title="About page — header">
            <Field label="Eyebrow text (small caps above headline)">
              <Input value={copy.aboutEyebrow} onChange={(v) => setCopy({ ...copy, aboutEyebrow: v })} />
            </Field>
            <Field label="Headline">
              <textarea
                value={copy.aboutHeadline}
                onChange={(e) => setCopy({ ...copy, aboutHeadline: e.target.value })}
                style={textareaStyle(80)}
              />
            </Field>
          </AdminCard>
        </div>

        <div style={{ marginTop: 16 }}>
          <AdminCard title="About page — body paragraphs">
            {copy.aboutParagraphs.length === 0 && (
              <div style={{ fontSize: 13, color: "var(--ink-soft)", marginBottom: 12 }}>No paragraphs yet.</div>
            )}
            {copy.aboutParagraphs.map((p, i) => (
              <div key={i} style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: "var(--ink-soft)", fontWeight: 600 }}>Paragraph {i + 1}</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => moveParagraph(i, -1)} disabled={i === 0} style={miniBtnStyle}>↑</button>
                    <button onClick={() => moveParagraph(i, 1)} disabled={i === copy.aboutParagraphs.length - 1} style={miniBtnStyle}>↓</button>
                    <button onClick={() => removeParagraph(i)} style={{ ...miniBtnStyle, color: "var(--accent)" }}>Remove</button>
                  </div>
                </div>
                <textarea
                  value={p}
                  onChange={(e) => updateParagraph(i, e.target.value)}
                  style={textareaStyle(96)}
                />
              </div>
            ))}
            <button onClick={addParagraph} style={addBtnStyle}>+ Add paragraph</button>
          </AdminCard>
        </div>

        <div style={{ marginTop: 16 }}>
          <AdminCard title="About page — FAQs">
            {copy.aboutFaqs.length === 0 && (
              <div style={{ fontSize: 13, color: "var(--ink-soft)", marginBottom: 12 }}>No FAQs yet.</div>
            )}
            {copy.aboutFaqs.map((f, i) => (
              <div key={i} style={{ marginBottom: 16, padding: 12, border: "1px solid var(--line)", borderRadius: 10 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: "var(--ink-soft)", fontWeight: 600 }}>FAQ {i + 1}</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => moveFaq(i, -1)} disabled={i === 0} style={miniBtnStyle}>↑</button>
                    <button onClick={() => moveFaq(i, 1)} disabled={i === copy.aboutFaqs.length - 1} style={miniBtnStyle}>↓</button>
                    <button onClick={() => removeFaq(i)} style={{ ...miniBtnStyle, color: "var(--accent)" }}>Remove</button>
                  </div>
                </div>
                <Field label="Question">
                  <Input value={f.q} onChange={(v) => updateFaq(i, "q", v)} />
                </Field>
                <Field label="Answer">
                  <textarea
                    value={f.a}
                    onChange={(e) => updateFaq(i, "a", e.target.value)}
                    style={textareaStyle(80)}
                  />
                </Field>
              </div>
            ))}
            <button onClick={addFaq} style={addBtnStyle}>+ Add FAQ</button>
          </AdminCard>
        </div>

        <div style={{ marginTop: 16 }}>
          <AdminCard title="Ride photos">
            <div style={{ fontSize: 13, color: "var(--ink-soft)", marginBottom: 14, lineHeight: 1.5 }}>
              Pick a synced ride, then add photos and captions for it. Photos show on the public{" "}
              <strong>/photos</strong> page, grouped by ride. The Strava sync never touches them.
            </div>
            {rides.length === 0 ? (
              <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>
                No synced rides yet. Connect Strava and sync first.
              </div>
            ) : (
              <>
                <Field label="Ride">
                  <select
                    value={selectedRideId}
                    onChange={(e) => setSelectedRideId(e.target.value)}
                    style={{ ...textareaStyle(0), minHeight: 0, padding: "10px 12px", resize: "none" }}
                  >
                    {rides.map((r) => (
                      <option key={r.rideId} value={r.rideId}>
                        {rideLabel(r, ridePhotosFor(r.rideId).length)}
                      </option>
                    ))}
                  </select>
                </Field>

                {selectedRideId && (
                  <div style={{ marginTop: 8 }}>
                    {ridePhotosFor(selectedRideId).length === 0 && (
                      <div style={{ fontSize: 13, color: "var(--ink-soft)", marginBottom: 12 }}>
                        No photos for this ride yet.
                      </div>
                    )}
                    {ridePhotosFor(selectedRideId).map((photo, i) => (
                      <div
                        key={photo.url}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "96px 1fr auto",
                          gap: 12,
                          alignItems: "center",
                          marginBottom: 10,
                          padding: 10,
                          border: "1px solid var(--line)",
                          borderRadius: 10,
                          background: "var(--bg)",
                        }}
                      >
                        <img
                          src={photo.url}
                          alt=""
                          style={{ width: 96, aspectRatio: "4/3", objectFit: "cover", borderRadius: 8, border: "1px solid var(--line)" }}
                        />
                        <Input
                          value={photo.caption}
                          onChange={(v) => updatePhotoCaption(selectedRideId, i, v)}
                        />
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => moveRidePhoto(selectedRideId, i, -1)} disabled={i === 0} style={miniBtnStyle}>↑</button>
                          <button
                            onClick={() => moveRidePhoto(selectedRideId, i, 1)}
                            disabled={i === ridePhotosFor(selectedRideId).length - 1}
                            style={miniBtnStyle}
                          >
                            ↓
                          </button>
                          <button onClick={() => removeRidePhoto(selectedRideId, i)} style={{ ...miniBtnStyle, color: "var(--accent)" }}>Remove</button>
                        </div>
                      </div>
                    ))}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        disabled={photoUploading}
                        onChange={(e) => {
                          const files = Array.from(e.target.files ?? []);
                          if (files.length) uploadRidePhotos(selectedRideId, files);
                          e.target.value = "";
                        }}
                        style={{ fontSize: 13 }}
                      />
                      {photoUploading && <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>Uploading…</span>}
                    </div>
                    {photoError && (
                      <div style={{ marginTop: 6, fontSize: 12, color: "var(--accent)" }}>Error: {photoError}</div>
                    )}
                  </div>
                )}
              </>
            )}
          </AdminCard>
        </div>

        <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12 }}>
          {saveMsg && (
            <span style={{ fontSize: 13, color: saveMsg.startsWith("Error") ? "var(--accent)" : "var(--ink-soft)" }}>
              {saveMsg}
            </span>
          )}
          <Btn variant="primary" onClick={saveCopy} disabled={savingCopy}>
            {savingCopy ? "Saving…" : "Save changes"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function rideLabel(r: Ride, photoCount: number): string {
  const date = r.dateIso
    ? new Date(r.dateIso).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "—";
  const count = photoCount === 0 ? "no photos" : `${photoCount} photo${photoCount === 1 ? "" : "s"}`;
  return `${date} · ${r.name || "Untitled ride"} — ${r.miles.toFixed(1)} mi  (${count})`;
}

const textareaStyle = (minHeight: number): CSSProperties => ({
  width: "100%",
  minHeight,
  padding: 12,
  fontFamily: "var(--ui)",
  fontSize: 14,
  border: "1px solid var(--line)",
  borderRadius: 10,
  background: "var(--bg)",
  color: "var(--ink)",
  resize: "vertical",
});

const miniBtnStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  padding: "2px 8px",
  border: "1px solid var(--line)",
  borderRadius: 6,
  background: "var(--bg)",
  color: "var(--ink)",
  cursor: "pointer",
};

const addBtnStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 14px",
  border: "1px dashed var(--line)",
  borderRadius: 10,
  background: "transparent",
  color: "var(--ink-soft)",
  cursor: "pointer",
};
